/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef, useState } from "preact/hooks";
import { getRandomId } from "../../utils/PRIVATE_visLayoutUtils";
import { cancelEvent } from "../../utils/UNSAFE_visUtils";

export type ItemInfo = {
    isFocusVisible?: boolean;
    itemIndex: number;
};

type Props = {
    getNextChartItem: (
        info: ItemInfo,
        arrow: 'ArrowLeft' | 'ArrowRight' | 'ArrowDown' | 'ArrowUp'
    ) => ItemInfo
};

export const getItemInfo = (event: Event) => {
    const itemIndex = (event.target as HTMLElement).dataset['ojItemIndex'];
    if (itemIndex) {
        return {
            itemIndex: parseInt(itemIndex, 10),
        };
    }
    return;
};

/**
 * Hook to handle chart navigation. This can be shared between the pie, funnel and pyramid chart
 * as they follow similar navigation pattern.
 * TODO: rename 
 */
export function usePieChartNav({
    getNextChartItem
}: Props) {

    const [focusedItemInfo, setfocusedItemInfo] = useState<ItemInfo>({
        itemIndex: 0
    });

    const [hoveredItemInfo, setHoveredItemInfo] = useState<ItemInfo>();
    const activeId = useRef<string>();

    const updateFocusedItemInfo = (itemInfo: ItemInfo) => {
        if (
            itemInfo.itemIndex !== focusedItemInfo.itemIndex ||
            itemInfo.isFocusVisible !== focusedItemInfo.isFocusVisible
        ) {
            setfocusedItemInfo(itemInfo);
            setHoveredItemInfo(undefined);
        }
    };

    const onPointerUp = (event: PointerEvent) => {
        const info = getItemInfo(event);
        if (info) {
            updateFocusedItemInfo({ ...info, isFocusVisible: false });
        }
    };

    const onPointerMove = (event: PointerEvent) => {
        const info = getItemInfo(event);
        if (info?.itemIndex != hoveredItemInfo?.itemIndex) {
            setHoveredItemInfo(info);
            activeId.current = getRandomId();
        }
    }

    const onKeyDown = (event: KeyboardEvent) => {
        const key = event.key;
        switch (key) {
            case 'Tab':
                return;
            case 'ArrowDown': {
                const info = getNextChartItem(focusedItemInfo, 'ArrowDown');
                updateFocusedItemInfo({ ...info, isFocusVisible: true });
                cancelEvent(event);
                break;
            }
            case 'ArrowUp': {
                const info = getNextChartItem(focusedItemInfo, 'ArrowUp');
                updateFocusedItemInfo({ ...info, isFocusVisible: true });
                cancelEvent(event);
                break;
            }
            case 'ArrowLeft': {
                const info = getNextChartItem(focusedItemInfo, 'ArrowLeft');
                updateFocusedItemInfo({ ...info, isFocusVisible: true });
                cancelEvent(event);
                break;
            }
            case 'ArrowRight': {
                const info = getNextChartItem(focusedItemInfo, 'ArrowRight');
                updateFocusedItemInfo({ ...info, isFocusVisible: true });
                cancelEvent(event);
                break;
            }
        }
        activeId.current = getRandomId();
    };

    const onKeyUp = (event: KeyboardEvent) => {
        const key = event.key;
        if (key === 'Tab') {
            updateFocusedItemInfo({ ...focusedItemInfo, isFocusVisible: true });
        }
    };

    const onBlur = () => {
        updateFocusedItemInfo({
            ...focusedItemInfo,
            isFocusVisible: false
        });
    };

    return {
        focusedItemInfo,
        hoveredItemInfo,
        activeId: activeId.current,
        navProps: {
            onKeyDown,
            onKeyUp,
            onPointerUp,
            onBlur,
            onPointerMove
        }
    }
}
